import React from 'react';
import * as XLSX from 'xlsx-js-style';
import { Shift } from 'domain/Shift';
import { Spot } from 'domain/Spot';
import { AppState } from 'store/types';
import { rosterOperations, rosterSelectors } from 'store/roster';
import { spotSelectors } from 'store/spot';
import { holidayOperations, holidaySelectors } from 'store/holiday';
import { connect } from 'react-redux';
import { alert } from 'store/alert';
import '@patternfly/react-core/dist/styles/base.css';
import {
  EmptyState, EmptyStateVariant, EmptyStateIcon, Title, EmptyStateBody, Form,
  Flex, FlexItem, Grid, GridItem, Text, Button, Tabs, Tab, TabTitleText,
  Card, CardTitle, CardBody, Accordion, AccordionItem, AccordionToggle,
  AccordionContent, Divider, TextContent, TextList, TextListItem,
} from '@patternfly/react-core';
import { ClockIcon, MapMarkerIcon, CalendarAltIcon, CubesIcon, DownloadIcon, UserIcon } from '@patternfly/react-icons';
import { withTranslation, WithTranslation, Trans } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { RosterState } from 'domain/RosterState';
import { ReportState } from 'domain/Reports';
import { Holiday } from 'domain/Holiday';
import DatePicker from 'react-datepicker';
import LoadingSpinner from 'ui/components/LoadingSpinner';
/* eslint-disable */

interface StateProps {
  tenantId: number;
  isLoading: boolean;
  allSpotList: Spot[];
  totalNumOfSpots: number;
  rosterState: RosterState | null;
  spotIdToShiftListMap: Map<number, Shift[]>;
  holidayList: Holiday[];
}

let lastSpotIdToShiftListMap: Map<number, Shift[]> = new Map<number, Shift[]>();

const mapStateToProps = (state: AppState): StateProps => ({
  tenantId: state.tenantData.currentTenantId,
  isLoading: rosterSelectors.isShiftRosterLoading(state),
  allSpotList: spotSelectors.getSpotList(state),
  totalNumOfSpots: spotSelectors.getSpotList(state).length,
  rosterState: state.rosterState.rosterState,
  spotIdToShiftListMap: lastSpotIdToShiftListMap = rosterSelectors.getSpotListInShiftRoster(state)
     .reduce(
       (prev, curr) => prev.set(
         curr.id as number,
         rosterSelectors.getShiftListForSpot(state, curr),
       ),
       // reducing an empty array returns the starting value
       rosterSelectors.isShiftRosterLoading(state) ? lastSpotIdToShiftListMap : new Map<number, Shift[]>(),
     ),
  holidayList: holidaySelectors.getHolidayList(state)
});

export interface DispatchProps {
  dispatch: Dispatch;
  // getShiftRoster: typeof rosterOperations.getShiftRoster;
  showInfoMessage: typeof alert.showInfoMessage;
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatch,
  // getShiftRoster: rosterOperations.getShiftRoster,
  showInfoMessage: alert.showInfoMessage,
});

export type Props = RouteComponentProps & StateProps & DispatchProps & WithTranslation;
interface State {
  isLoading: boolean;
  dateError: string;
  activeTabKey: number;
  formData: Partial<ReportState>;
  expandedItems: Record<string, boolean>;
}

export function isFormCompleted(rs: Partial<ReportState>): rs is ReportState {
  return rs.toDate !== null && rs.fromDate !== null;
}

export class ReportsPage extends React.Component<Props, State> {
  state: State = {
    isLoading: false,
    dateError: '',
    activeTabKey: 0,
    formData: { fromDate: null, toDate: null },
    expandedItems: {}
  };

  handleToggle = (key: string) => {
    this.setState((prevState) => ({
      expandedItems: {
        ...prevState.expandedItems,
        [key]: !prevState.expandedItems[key],
      },
    }));
  };
  

  handleTabClick = (_event: React.MouseEvent<any>, tabIndex: number) => {
    this.setState({ activeTabKey: tabIndex });
  };

  setProperty = (properties: Partial<ReportState>) => {
    this.setState((prevState) => ({
      formData: { ...prevState.formData, ...properties },
    }));
  };

  private paintSpecialRows(
    worksheet: XLSX.WorkSheet,
    rows: any[],
    columnCount: number
  ) {
    rows.forEach((row, index) => {
      if (!row.__specialDay) {
        return;
      }

      const excelRow = index + 2; // fila 1 = encabezados

      for (let colIndex = 0; colIndex < columnCount; colIndex += 1) {
        const cellAddress = XLSX.utils.encode_cell({
          r: excelRow - 1,
          c: colIndex,
        });

        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            fill: {
              patternType: 'solid',
              fgColor: { rgb: 'EBC7C7' },
            },
          };
        }
      }
    });
  }

  // Exportar por empleado
  exportToExcelByEmployee = () => {
    const rows: any[] = [];

    Array.from(this.props.spotIdToShiftListMap.entries()).forEach(([_, shiftList]) => {
      shiftList.forEach(shift => {
        const employeeName = shift.originalEmployee == null
        ? 'Empleado desconocido'
        : (shift.employee?.name ?? 'Empleado desconocido');
        const spotName = shift.spot?.name ?? 'Puesto desconocido';
        const start = new Date(shift.startDateTime);
        const end = new Date(shift.endDateTime);
        const hours = ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

        rows.push({
          Empleado: employeeName,
          Fecha: this.formatServiceDate(start),
          Horas: hours,
          Ubicación: spotName,
          __specialDay: this.isHolidayOrSunday(start),
        });
      });
    });

    const exportRows = rows.map(({ __specialDay, ...row }) => row);
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    this.paintSpecialRows(worksheet, rows, 4);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte por Empleado');
    XLSX.writeFile(workbook, 'reporte_por_empleado.xlsx');
  };

  // Exportar por spot
  exportToExcelBySpot = () => {
    const rows: any[] = [];

    Array.from(this.props.spotIdToShiftListMap.entries()).forEach(([spotId, shiftList]) => {
      const spot = this.props.allSpotList.find(s => s.id === spotId);
      const spotName = spot?.name ?? `Spot ${spotId}`;

      shiftList.forEach(shift => {
        const start = new Date(shift.startDateTime);
        const end = new Date(shift.endDateTime);
        const hours = ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const employeeName = shift.originalEmployee == null
          ? 'Empleado desconocido'
          : (shift.employee?.name ?? 'Empleado desconocido');

        rows.push({
          Ubicación: spotName,
          Fecha: this.formatServiceDate(start),
          Horas: hours,
          Empleado: employeeName,
          __specialDay: this.isHolidayOrSunday(start),
        });
      });
    });

    const exportRows = rows.map(({ __specialDay, ...row }) => row);
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    this.paintSpecialRows(worksheet, rows, 4);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte por Puesto');
    XLSX.writeFile(workbook, 'reporte_por_puesto.xlsx');
  };

  private getDateKey(value: Date | string): string {
    if (typeof value === 'string') {
      return value.substring(0, 10);
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private isHolidayOrSunday(date: Date): boolean {
    const dateKey = this.getDateKey(date);

    const isSunday = date.getDay() === 0;

    const isHoliday = this.props.holidayList.some((holiday) =>
      this.getDateKey(holiday.date as any) === dateKey
    );

    return isSunday || isHoliday;
  }

  private formatServiceDate(date: Date): string {
    const dateText = date.toLocaleDateString('es-CO');
    const dayName = date.toLocaleDateString('es-CO', { weekday: 'long' });

    return `${dateText} - ${dayName}`;
  }

  render() {
    const { t } = this.props;
    const { activeTabKey, formData } = this.state;
    
    const employeeIdToShiftListMap = new Map<number, Shift[]>();
    this.props.spotIdToShiftListMap.forEach((shiftList) => {
      shiftList.forEach((shift) => {
        const employee = shift.employee;
    
        if (employee && typeof employee.id === 'number') {
          const employeeId = employee.id;
    
          if (!employeeIdToShiftListMap.has(employeeId)) {
            employeeIdToShiftListMap.set(employeeId, []);
          }
    
          employeeIdToShiftListMap.get(employeeId)!.push(shift);
        }
      });
    });
    
    const isEmptyMap = Array.from(this.props.spotIdToShiftListMap.values()).every(arr => arr.length === 0);

    const EmptyReport = (
      <EmptyState variant={EmptyStateVariant.full}>
        <EmptyStateIcon icon={CubesIcon} />
        <Trans
          t={t}
          i18nKey="noReports"
          components={[
            <Title headingLevel="h5" size="lg" key={0} />,
            <EmptyStateBody key={1} />,
          ]}
        />
      </EmptyState>
    );

    return (
      <>
        { (this.state.isLoading) ? <LoadingSpinner isLoading /> : '' }
        <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', minWidth: '200px', flexGrow: 1 }}>
            <Form onSubmit={(e) => e.preventDefault()}>
              <h1 style={{ fontSize: '1.2rem' }}>Consultar fechas</h1>
              <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <FlexItem>
                  <Grid>
                    <GridItem span={5}>
                      <Text>{t('fromDate')}</Text>
                    </GridItem>
                    <GridItem span={7}>
                      <DatePicker
                        aria-label={t('fromDate')}
                        selected={formData.fromDate}
                        placeholderText='mm/dd/aaaa'
                        onChange={(from) => this.setProperty({ fromDate: from })}
                      />
                    </GridItem>
                  </Grid>
                </FlexItem>
                <FlexItem>
                  <Grid>
                    <GridItem span={5}>
                      <Text>{t('toDate')}</Text>
                    </GridItem>
                    <GridItem span={7}>
                      <DatePicker
                        aria-label={t('toDate')}
                        selected={formData.toDate}
                        placeholderText='mm/dd/aaaa'
                        onChange={(end) => this.setProperty({ toDate: end })}
                      />
                      {this.state.dateError && (
                        <div style={{ color: 'red', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                          {this.state.dateError}
                        </div>
                      )}
                    </GridItem>
                  </Grid>
                </FlexItem>
                <FlexItem>
                  <GridItem>
                    <Button
                      isDisabled={!isFormCompleted(formData)}
                      aria-label="Save"
                      data-cy="save"
                      key={0}
                      onClick={() => {
                        const { fromDate, toDate } = formData;
                        if (!fromDate?.getTime() || !toDate?.getTime()) {
                          this.setState({ dateError: 'Debe completar ambas fechas' });
                          return;
                        }
                      
                        if (fromDate.getTime() > toDate.getTime()) {
                          this.setState({ dateError: 'La fecha de inicio no puede ser posterior a la fecha final' });
                          return;
                        }

                        if (isFormCompleted(formData)) {
                          this.setState({ dateError: '', isLoading: true });
                          
                          Promise.all([
                            this.props.dispatch(holidayOperations.refreshHolidayList() as any),

                            this.props.dispatch(rosterOperations.getShiftRosterReport({
                              fromDate: fromDate!,
                              toDate: toDate!,
                            }) as any),
                          ]).then(() => {
                            this.setState({ isLoading: false });
                          }).catch((error: any) => {
                            console.log(error);
                            this.setState({ isLoading: false });
                          });
                        }
                      }}
                    >
                    {t('search')}
                    </Button>
                  </GridItem>
                </FlexItem>
              </Flex>
            </Form>
          </div>
          <div style={{ backgroundColor: 'white', padding: '1rem', minWidth: '0', overflow: 'hidden', flexGrow: 3 }}>
            <Tabs
              activeKey={activeTabKey}
              onSelect={this.handleTabClick}
              aria-label="Tabs in the default example"
              role="region"
            >
              <Tab eventKey={0} title={<TabTitleText>{t('employees')}</TabTitleText>} aria-label="Default content - users">
              {isEmptyMap ? (
                EmptyReport
              ) : (
                <Accordion isBordered style={{ paddingTop: '1rem' }}>
                  <Button variant="primary" onClick={this.exportToExcelByEmployee} style={{ marginBottom: '1rem' }} alt='Exportar a excel'>
                    <DownloadIcon />
                  </Button>
                  {Array.from(employeeIdToShiftListMap.entries()).map(([employeeId, shiftList]) => {
                    const employee = shiftList[0].employee;
                    if (!employee || !employee.id) return null;

                    const employeeKey = `emp-${employee.id}`;
                    const employeeName = employee.name ?? 'Empleado sin nombre';

                    // Agrupar por spot
                    const spotGroups = new Map<number, Shift[]>();
                    shiftList.forEach((shift) => {
                      const spotId = shift.spot?.id;
                      if (spotId === undefined) return;
                      if (!spotGroups.has(spotId)) {
                        spotGroups.set(spotId, []);
                      }
                      spotGroups.get(spotId)!.push(shift);
                    });

                    const totalHours = shiftList.reduce((acc: number, shift: Shift) => {
                      const start = new Date(shift.startDateTime).getTime();
                      const end = new Date(shift.endDateTime).getTime();
                      return acc + (end - start) / (1000 * 60 * 60);
                    }, 0);

                    return (
                      <AccordionItem key={employeeKey}>
                        <AccordionToggle
                          onClick={() => this.handleToggle(employeeKey)}
                          isExpanded={this.state.expandedItems[employeeKey] || false}
                          id={`toggle-${employeeKey}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ flex: 1, minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                              <Title headingLevel="h4" size="lg">
                                <UserIcon /> {employeeName}
                              </Title>
                            </div>
                            <div style={{ flexShrink: 0, marginLeft: '1rem', width: '150px', textAlign: 'left' }}>
                              <Text component="small" style={{ marginLeft: '1rem' }}>
                                <ClockIcon /> Total: {totalHours.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} horas
                              </Text>
                            </div>
                          </div>
                        </AccordionToggle>
                        <AccordionContent
                          id={`content-${employeeKey}`}
                          isHidden={!this.state.expandedItems[employeeKey]}
                        >
                          {Array.from(spotGroups.entries()).map(([spotId, shifts]) => {
                            const spotName = shifts[0].spot?.name ?? 'Puesto desconocido';
                            const spotHours = shifts.reduce((acc: number, shift: Shift) => {
                              const start = new Date(shift.startDateTime).getTime();
                              const end = new Date(shift.endDateTime).getTime();
                              return acc + (end - start) / (1000 * 60 * 60);
                            }, 0);

                            return (
                              <Card key={spotId} isCompact style={{ marginBottom: '1rem' }}>
                                <CardTitle>
                                  <MapMarkerIcon /> {spotName} — <ClockIcon /> {spotHours.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
                                </CardTitle>
                                <Divider />
                                <CardBody>
                                  <TextContent>
                                    <TextList isPlain>
                                      {shifts.map((shift, index) => {
                                        const start = new Date(shift.startDateTime);
                                        const end = new Date(shift.endDateTime);
                                        const hours = ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                        return (
                                          // <TextListItem key={index}>
                                          //   <CalendarAltIcon style={{ marginRight: '0.5rem' }} />
                                          //   {start.toLocaleDateString()} {'('} {start.toLocaleTimeString()} - {end.toLocaleTimeString()} {')'} — {hours} h
                                          // </TextListItem>

                                          <TextListItem
                                            key={index}
                                            style={{
                                              backgroundColor: this.isHolidayOrSunday(start) ? '#fff5f5' : undefined,
                                              padding: '0.5rem',
                                              borderRadius: '6px',
                                            }}
                                          >
                                            <CalendarAltIcon style={{ marginRight: '0.5rem' }} />
                                            {this.formatServiceDate(start)} {'('} {start.toLocaleTimeString()} - {end.toLocaleTimeString()} {')'} — {hours} h
                                          </TextListItem>
                                        );
                                      })}
                                    </TextList>
                                  </TextContent>
                                </CardBody>
                              </Card>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}

              </Tab>
              <Tab eventKey={1} title={<TabTitleText>{t('spots')}</TabTitleText>}>
              {isEmptyMap ? (
                EmptyReport
              ) : (
                <Accordion isBordered style={{ paddingTop: '1rem' }}>
                  <Button variant="primary" onClick={this.exportToExcelBySpot} style={{ marginBottom: '1rem' }}>
                    <DownloadIcon />
                  </Button>
                {Array.from(this.props.spotIdToShiftListMap.entries())
                  .filter(([_, shiftList]) => {
                    const totalHours = shiftList.reduce((sum, shift) => {
                      const start = new Date(shift.startDateTime);
                      const end = new Date(shift.endDateTime);
                      return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }, 0);
                    return totalHours > 0;
                  })
                  .map(([spotId, shiftList]) => {
                    const totalHours = shiftList.reduce((sum, shift) => {
                      const start = new Date(shift.startDateTime);
                      const end = new Date(shift.endDateTime);
                      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                      return sum + hours;
                    }, 0);
              
                    const spot = this.props.allSpotList.find(s => s.id === spotId);
                    const spotName = spot ? spot.name : `Spot ${spotId}`;
                    const spotKey = `spot-${spotId}`;
              
                    return (
                      <AccordionItem key={spotKey}>
                        <AccordionToggle
                          onClick={() => this.handleToggle(spotKey)}
                          isExpanded={this.state.expandedItems[spotKey] || false}
                          id={`toggle-${spotKey}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <div style={{ flex: 1, minWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                            <Title headingLevel="h4" size="lg">
                              <MapMarkerIcon style={{ marginRight: '0.5rem' }} /> {spotName}
                            </Title>
                          </div>
                          <div style={{ flexShrink: 0, marginLeft: '1rem', width: '150px', textAlign: 'left' }}>
                            <Text component="small">
                              <ClockIcon style={{ marginRight: '0.3rem' }} />
                              Total: {totalHours.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} horas
                            </Text>
                          </div>
                          </div>
                        </AccordionToggle>
              
                        <AccordionContent
                          id={`content-${spotKey}`}
                          isHidden={!this.state.expandedItems[spotKey]}
                        >
                          <Card isCompact style={{ marginTop: '1rem' }}>
                            <CardBody>
                              <TextContent>
                                <TextList isPlain>
                                  {shiftList.map((shift, index) => {
                                    const start = new Date(shift.startDateTime);
                                    const end = new Date(shift.endDateTime);
                                    const hours = ((end.getTime() - start.getTime()) / (1000 * 60 * 60)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    const employeeName = shift.employee?.name ?? 'Empleado desconocido';
              
                                    return (
                                      <TextListItem
                                        key={index}
                                        style={{
                                          backgroundColor: this.isHolidayOrSunday(start) ? '#fff5f5' : undefined,
                                          padding: '0.5rem',
                                          borderRadius: '6px',
                                        }}
                                      >
                                        <CalendarAltIcon style={{ marginRight: '0.5rem' }} />
                                        {this.formatServiceDate(start)} {'('} {start.toLocaleTimeString()} - {end.toLocaleTimeString()} {')'} — {hours} h — {employeeName}
                                      </TextListItem>
                                    );
                                  })}
                                </TextList>
                              </TextContent>
                            </CardBody>
                          </Card>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
              </Accordion>
              )}
              </Tab>
            </Tabs>
          </div>
        </div>
      </>
    );
  };
};

export default withTranslation('ReportsPage')(connect(mapStateToProps, mapDispatchToProps)(withRouter(ReportsPage)));
